import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { useCreateOpportunityContactRole, useContacts } from '@/hooks/use-api'
import type { OpportunityContactRole } from '@/types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { explainError } from '@/lib/explain-error'

interface CreateOpportunityContactRoleModalProps {
  opportunityId: string
  accountId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const CONTACT_ROLES = [
  'Business User',
  'Decision Maker',
  'Economic Buyer',
  'Economic Decision Maker',
  'Executive Sponsor',
  'Influencer',
  'Other',
  'Technical Buyer',
]

export function CreateOpportunityContactRoleModal({
  opportunityId,
  accountId,
  open,
  onOpenChange,
}: CreateOpportunityContactRoleModalProps) {
  const { toast } = useToast()
  const createMutation = useCreateOpportunityContactRole()
  const [contactSearch, setContactSearch] = useState('')
  const debouncedContactSearch = useDebounce(contactSearch, 300)
  const { data: contactsData, isFetching: contactsLoading } = useContacts({
    search: debouncedContactSearch || undefined,
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc',
    account_id: accountId || undefined,
    status__c: 'Active',
  })

  const [formData, setFormData] = useState<Partial<OpportunityContactRole>>({
    opportunity_id: opportunityId,
    contact_id: '',
    role: undefined,
    is_primary: false,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const contacts = [...(contactsData?.data || [])].sort((a, b) => {
    const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase()
    const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase()
    return nameA.localeCompare(nameB)
  })

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.contact_id) {
      newErrors.contact_id = 'Contact is required'
    }

    if (!formData.role) {
      newErrors.role = 'Role is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      await createMutation.mutateAsync({
        opportunityId,
        data: {
          contact_id: formData.contact_id,
          role: formData.role,
          is_primary: formData.is_primary || false,
        },
      })

      toast({
        title: 'Success',
        description: 'Contact role added successfully.',
      })

      // Reset form and close modal
      setFormData({
        opportunity_id: opportunityId,
        contact_id: '',
        role: undefined,
        is_primary: false,
      })
      setErrors({})
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to add contact role')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-contact-role-description"
      >
        <DialogHeader>
          <DialogTitle>Add Contact Role</DialogTitle>
          <DialogDescription id="create-contact-role-description">
            Add a contact role to this opportunity. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Selection */}
          <div className="space-y-2">
            <Label htmlFor="contact" className="required">
              Contact *
            </Label>
            <RecordCombobox
              id="contact"
              records={contacts}
              value={formData.contact_id || ''}
              onValueChange={(value) => handleChange('contact_id', value)}
              placeholder="Select a contact"
              searchPlaceholder="Search contacts..."
              emptyMessage="No contacts found."
              displayField={(c) => {
                const name = `${c.first_name || ''} ${c.last_name || ''}`.trim()
                const parts = [name]
                if (c.title) parts.push(c.title)
                if (c.account_name) parts.push(`(${c.account_name})`)
                return parts.join(' - ')
              }}
              aria-label="Contact"
              onSearchChange={setContactSearch}
              isLoading={contactsLoading}
              totalCount={contactsData?.total}
            />
            {errors.contact_id && (
              <p id="contact-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.contact_id}
              </p>
            )}
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="role" className="required">
              Role *
            </Label>
            <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
              <SelectTrigger
                id="role"
                aria-required="true"
                aria-invalid={!!errors.role}
                aria-describedby={errors.role ? 'role-error' : undefined}
              >
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && (
              <p id="role-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.role}
              </p>
            )}
          </div>

          {/* Primary Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_primary"
              checked={formData.is_primary}
              onCheckedChange={(checked) => handleChange('is_primary', checked)}
              aria-label="Set as primary contact for this opportunity"
            />
            <Label
              htmlFor="is_primary"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Primary Contact
            </Label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Adding...' : 'Add Contact Role'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
